import { useEffect } from 'react';
import { Request } from 'express';
import debounce from 'lodash/debounce';

import { Article } from 'editorial/components/Article';
import { IArticle } from 'editorial/types';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useMParticleContext } from 'tracking/hooks/useMParticleContext';
import { cmsRequest } from 'server/services/cms-service/cmsRequest';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { LOCALES, OMITTED_EDITORIAL_LOCALES } from 'localization/constants/configEnums';

export const EditorialArticle = ({ article, nextArticle }) => {
  const { isMParticleReady } = useMParticleContext();

  useEffect(() => {
    if (isMParticleReady) {
      sendTrackingEvent('ARTICLE_VIEW');
    }
  }, [isMParticleReady]);

  const handleScroll = debounce(() => {
    sendTrackingEvent('ARTICLE_USER_SCROLL');
  }, 50);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return <Article article={article} nextArticle={nextArticle} />;
};

export const getServerSideProps = async (ctx) => {
  const {
    req,
    query: { slug },
    locale,
  }: {
    req: Request;
    query: { slug: string };
    locale: string;
  } = ctx;

  // TEMPORARY REDIRECTS until we have a proper redirect system in place from the CMS
  const redirectSlugs = [
    'division-street-teams-up-with-goat-and-flight-club-to-release-nike-dunk-low-what-the-duck-in-two-unique-colorways',
    'flight-club-celebrates-the-fifth-year-of-its-flight-club-fridays-music-series',
    'goat-group-releases-its-second-annual-alias-seller-report',
    'announcing-goat-as-art-basel-s-official-global-uniform-partner',
    'goat-launches-ar-try-on-of-rare-sneakers',
    'goat-group-leads-60m-series-b-funding-in-grailed',
    'goat-invites-you-to-explore-products-across-time-during-black-friday-auctions',
    'division-street-and-goat-partner-to-auction-exclusive-tinker-hatfield-designed-air-jordan-viii-university-of-oregon-pes-to-benefit-ducks-student-athletes',
    'goat-launches-bf23-event-featuring-globally-themed-fashion-experience',
    'global-mexican-music-artist-peso-pluma-pro-skater-tyshawn-jones-and-fashion-pioneer-yoon-ahn-debut-as-cover-stars-on-the-ninth-issue-of-greatest-magazine',
    'greatest-magazine-returns-with-its-eighth-issue-featuring-iconic-music-duo-clipse-and-erl-designer-eli-russell-linnetz',
  ];

  if (redirectSlugs.includes(slug)) {
    return {
      redirect: {
        destination: `https://www.goatgroup.com/news/${slug}`,
        statusCode: 308,
      },
    };
  }

  let nextArticle = null;

  if (OMITTED_EDITORIAL_LOCALES.includes(locale as LOCALES)) {
    return { notFound: true };
  }

  try {
    const data = cmsRequest(req).get(`api/v1/cms/cms_articles/${slug}`);
    const article: IArticle = await data.json();

    if (article?.category) {
      const nextArticleData = cmsRequest(req).get(
        `api/v1/cms/cms_articles/${slug}/next?category=${article.category}`,
      );
      nextArticle = await nextArticleData.json();
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        article,
        nextArticle,
      },
    };
  } catch {
    return {
      redirect: {
        destination: getRedirectionPath('/editorial', locale),
        permanent: false,
      },
    };
  }
};

export default EditorialArticle;
